import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const ContactCTA = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    budget: '',
    timeline: '',
    message: '',
    newsletter: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const serviceOptions = [
    { value: 'mvp', label: 'MVP Development' },
    { value: 'full-product', label: 'Full Product Development' },
    { value: 'enterprise', label: 'Enterprise Solution' },
    { value: 'mobile', label: 'Mobile App Development' },
    { value: 'consulting', label: 'Technical Consulting' },
    { value: 'maintenance', label: 'Ongoing Maintenance' }
  ];

  const budgetOptions = [
    { value: 'under-25k', label: 'Under $25,000' },
    { value: '25k-50k', label: '$25,000 - $50,000' },
    { value: '50k-100k', label: '$50,000 - $100,000' },
    { value: '100k-250k', label: '$100,000 - $250,000' },
    { value: 'over-250k', label: 'Over $250,000' }
  ];

  const timelineOptions = [
    { value: 'urgent', label: 'Urgent (1-2 months)' },
    { value: 'short', label: 'Short-term (3-6 months)' },
    { value: 'medium', label: 'Medium-term (6-12 months)' },
    { value: 'long', label: 'Long-term (12+ months)' },
    { value: 'flexible', label: 'Flexible' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.name?.trim()) newErrors.name = 'Name is required';
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData?.service) newErrors.service = 'Please select a service';
    if (!formData?.message?.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const newErrors = validateForm();
    
    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    
    setTimeout(() => {
      console.log('Form submitted:', formData);
      alert('Thank you for your inquiry! Our team will contact you within 24 hours.');
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        service: '',
        budget: '',
        timeline: '',
        message: '',
        newsletter: false
      });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-gradient-to-br from-primary/5 via-background to-accent/5" id="contact-form">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-start">
          <div>
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4 md:mb-6">
              <Icon name="MessageSquare" size={18} color="var(--color-primary)" />
              <span className="text-xs md:text-sm font-medium font-cta text-primary">
                Get In Touch
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-headline text-foreground mb-4 md:mb-6">
              Start Your Transformation Journey
            </h2>
            <p className="text-base md:text-lg text-muted-foreground font-body mb-8 md:mb-10 leading-relaxed">
              Ready to transform your vision into reality? Schedule a free consultation with our experts to discuss your project requirements and receive a detailed proposal.
            </p>

            <div className="space-y-6 md:space-y-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Clock" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold font-headline text-foreground mb-2">
                    Quick Response Time
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground font-body">
                    We respond to all inquiries within 24 hours with a detailed assessment of your requirements.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Shield" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold font-headline text-foreground mb-2">
                    Confidentiality Guaranteed
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground font-body">
                    Your ideas and information are protected under strict NDA agreements.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Users" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold font-headline text-foreground mb-2">
                    Expert Consultation
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground font-body">
                    Connect directly with senior developers and architects who understand your challenges.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 md:mt-10 p-6 bg-card border border-border rounded-lg">
              <h3 className="text-lg font-semibold font-headline text-foreground mb-4 flex items-center space-x-2">
                <Icon name="Phone" size={20} />
                <span>Prefer to Talk?</span>
              </h3>
              <p className="text-sm md:text-base text-muted-foreground font-body mb-4">
                Schedule a call with our team to discuss your project in detail.
              </p>
              <a
                href="tel:+91 9811143590"
                className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 font-medium font-cta text-sm md:text-base transition-colors duration-200"
              >
                <Icon name="Phone" size={18} />
                <span>+91 9811143590</span>
              </a>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 md:p-8 shadow-xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <Input
                  label="Full Name"
                  type="text"
                  name="name"
                  placeholder="John Doe"
                  value={formData?.name}
                  onChange={handleChange}
                  error={errors?.name}
                  required
                />
                <Input
                  label="Email Address"
                  type="email"
                  name="email"
                  placeholder="john@company.com"
                  value={formData?.email}
                  onChange={handleChange}
                  error={errors?.email}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <Input
                  label="Company Name"
                  type="text"
                  name="company"
                  placeholder="Your Company"
                  value={formData?.company}
                  onChange={handleChange}
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  name="phone"
                  placeholder="Mobile Number"
                  value={formData?.phone}
                  onChange={handleChange}
                />
              </div>

              <Select
                label="Service Interested In"
                options={serviceOptions}
                value={formData?.service}
                onChange={(value) => handleSelectChange('service', value)}
                placeholder="Select a service"
                error={errors?.service}
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-1 gap-4 md:gap-6">
                {/* <Select
                  label="Project Budget"
                  options={budgetOptions}
                  value={formData?.budget}
                  onChange={(value) => handleSelectChange('budget', value)}
                  placeholder="Select budget range"
                /> */}
                <Select
                  label="Timeline"
                  options={timelineOptions}
                  value={formData?.timeline}
                  onChange={(value) => handleSelectChange('timeline', value)}
                  placeholder="Select timeline"
                />
              </div>

              <div>
                <label className="block text-sm font-medium font-body text-foreground mb-2">
                  Project Details <span className="text-destructive">*</span>
                </label>
                <textarea
                  name="message"
                  rows="5"
                  placeholder="Tell us about your project requirements, goals, and any specific challenges you're facing..."
                  value={formData?.message}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-md border font-body text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-primary transition-colors duration-200 ${
                    errors?.message
                      ? 'border-destructive focus:ring-destructive' :'border-border bg-background text-foreground'
                  }`}
                />
                {errors?.message && (
                  <p className="mt-1 text-xs md:text-sm text-destructive font-body">
                    {errors?.message}
                  </p>
                )}
              </div>

              {/* <Checkbox
                label="Subscribe to our newsletter for SaaS development insights and industry trends"
                checked={formData?.newsletter}
                onChange={handleChange}
                name="newsletter"
              /> */}

              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                loading={isSubmitting}
                iconName="Send"
                iconPosition="right"
              >
                {isSubmitting ? 'Sending...' : 'Send Inquiry'}
              </Button>

              <p className="text-xs md:text-sm text-muted-foreground font-body text-center">
                By submitting this form, you agree to our privacy policy and terms of service.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactCTA;